// Diagnostic Viewer Helper for TypeScript Component
// This helper generates and opens the diagnostic viewer in a new window
// The HTML/CSS/JS content is based on the standalone files in this folder

export interface DiagnosticData {
  [key: string]: any;
}

export class DiagnosticViewerHelper {
  private static readonly COLUMN_KEYS = [
    'User',
    'Date',
    'Client',
    'Study',
    'Message ID',
    'Question',
    'mcode',
    'Liked',
    'Like Reason',
    'Dislike Reason',
    'Total Base',
    'Row',
    'Row Nesting',
    'Column',
    'Column Nesting',
    'Filters',
    'Section',
    'Snapshot Name',
    'Snapshot Type',
    'Entry Point',
    'Widget ID',
    'Widget Name',
    'Primary Brands',
    'Primary Metrics',
    'Time Period',
    'Audience Filter'
  ];

  /**
   * Opens the diagnostic viewer in a new window with the provided data
   * @param data Array of diagnostic data objects
   */
  static displayDiagnosticData(data: DiagnosticData[]): void {
    const newTab = window.open('', '_blank');
    if (!newTab) {
      alert('Unable to open a new tab. Please check if pop-ups are blocked.');
      return;
    }

    if (!data || data.length === 0) {
      this.displayNoDataPage(newTab);
      return;
    }

    this.displayDataPage(newTab, data);
  }

  /**
   * Displays a page when no data is available
   */
  private static displayNoDataPage(newTab: Window): void {
    newTab.document.write(`
      <html>
        <head>
          <title>ARK Diagnostics Data</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
          </style>
        </head>
        <body>
          <h2>No diagnostic data available</h2>
        </body>
      </html>
    `);
    newTab.document.close();
  }

  /**
   * Displays the full diagnostic viewer page with data
   */
  private static displayDataPage(newTab: Window, data: DiagnosticData[]): void {
    const keyMapping: Record<string, string> = {
      'UserEmail': 'User',
      'clientName': 'Client',
      'studyName': 'Study',
      'MessageId': 'Message ID',
      'user_input': 'Question',
      'IsLiked': 'Liked',
      'LikeReason': 'Like Reason',
      'DislikeReason': 'Dislike Reason',
      'isTotalBase': 'Total Base',
      'Row_Nesting': 'Row Nesting',
      'Column_Nesting': 'Column Nesting',
      'Filter': 'Filters',
      'Snapshot_Name': 'Snapshot Name',
      'Snapshot_Type': 'Snapshot Type',
      'Widget_ID': 'Widget ID',
      'Widget_Name': 'Widget Name',
      'primary_brands': 'Primary Brands',
      'primary_metrics': 'Primary Metrics',
      'market': 'Entry Point',
      'timeperiod': 'Time Period',
      'Filter_Audience': 'Audience Filter'
    };

    const mappedData = data.map(item => {
      const newItem: DiagnosticData = {};
      for (const [key, value] of Object.entries(item)) {
        const newKey = keyMapping[key] || key;
        newItem[newKey] = value;
      }
      return newItem;
    });

    const fullHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>ARK Diagnostics Data</title>
          <style>${this.getCSS()}</style>
        </head>
        <body>
          ${this.getHTML()}
          
          <script>${this.getScript()}</script>
          <script>
            // Initialize the viewer with data
            if (window.initializeDiagnosticViewer) {
              const data = ${JSON.stringify(mappedData).replace(/</g, '\\u003c').replace(/>/g, '\\u003e')};
              const columnKeys = ${JSON.stringify(this.COLUMN_KEYS)};
              window.initializeDiagnosticViewer(data, columnKeys);
            }
          </script>
        </body>
      </html>
    `;

    newTab.document.write(fullHTML);
    newTab.document.close();
  }

  /**
   * Returns the HTML structure for the diagnostic viewer
   * Based on diagnostic-viewer.html
   */
  private static getHTML(): string {
    return `
      <div class="search-container">
        <input type="text" placeholder="Search..." class="autocomplete" id="searchInput"/>
        <button id="downloadExcelBtn" class="download-btn">Download Excel</button>
      </div>

      <div class="pagination-container">
        <div class="pagination-info" id="paginationInfo"></div>
        <div class="pagination-controls">
          <button class="pagination-btn" id="firstBtn">First</button>
          <button class="pagination-btn" id="prevBtn">Previous</button>
          <span>Page</span>
          <input type="number" class="page-input" id="pageInput" min="1" value="1">
          <span id="totalPagesLabel">of 1</span>
          <button class="pagination-btn" id="nextBtn">Next</button>
          <button class="pagination-btn" id="lastBtn">Last</button>
        </div>
      </div>

      <div class="table-container">
        <table id="diagnosticTable">
          <thead>
            <tr id="tableHeader">
              <!-- Headers will be dynamically generated -->
            </tr>
          </thead>
          <tbody id="tableBody"></tbody>
        </table>
      </div>
    `;
  }

  /**
   * Returns the CSS styles for the diagnostic viewer
   * Based on diagnostic-viewer.css
   */
  private static getCSS(): string {
    return `
      body {
        font-family: Arial, sans-serif;
        margin: 20px;
        background-color: #f5f5f5;
      }

      .download-btn {
        padding: 10px 15px;
        background-color: #4CAF50;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        float: right;
        margin-right: 20px;
      }

      .download-btn:hover {
        background-color: #45a049;
      }

      .search-container {
        margin-bottom: 20px;
      }

      .autocomplete {
        width: 300px;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
      }

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: nowrap;
        width: 100%;
      }
      .filter-toggle-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        opacity: 0.8;
        transition: opacity 0.2s;
        flex-shrink: 0;
      }
      .filter-toggle-btn:hover, .filter-toggle-btn.active {
        opacity: 1;
      }
      .filter-toggle-btn svg {
        flex-shrink: 0;
      }
      .column-filter-input {
        width: 100%;
        padding: 5px;
        font-size: 12px;
        border: 1px solid #ddd;
        border-radius: 3px;
        margin-top: 5px;
        box-sizing: border-box;
      }

      .pagination-container {
        margin: 20px 0;
        text-align: center;
      }

      .pagination-info {
        margin: 10px 0;
        font-size: 14px;
        color: #666;
      }

      .pagination-controls {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        margin: 10px 0;
      }

      .pagination-btn {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        font-size: 14px;
      }

      .pagination-btn:hover {
        background-color: #f0f0f0;
      }

      .pagination-btn:disabled {
        background-color: #e9e9e9;
        color: #999;
        cursor: not-allowed;
      }

      .page-input {
        width: 60px;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        text-align: center;
        font-size: 14px;
      }

      .table-container {
        max-height: 85vh;
        overflow: auto;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        position: relative;
      }

      table {
        width: max-content;
        min-width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 13px;
        table-layout: fixed;
      }

      /* Fixed columns styling */
      th:nth-child(1), td:nth-child(1) {
        position: sticky;
        left: 0;
        z-index: 20;
        background-color: white;
        border-right: 2px solid #4CAF50;
        min-width: 200px;
        max-width: 200px;
        width: 200px;
      }

      th:nth-child(2), td:nth-child(2) {
        position: sticky;
        left: 200px;
        z-index: 20;
        background-color: white;
        border-right: 2px solid #4CAF50;
        min-width: 200px;
        max-width: 200px;
        width: 200px;
      }

      th:nth-child(3), td:nth-child(3) {
        position: sticky;
        left: 400px;
        z-index: 20;
        background-color: white;
        border-right: 2px solid #4CAF50;
        min-width: 200px;
        max-width: 200px;
        width: 200px;
      }

      th:nth-child(4), td:nth-child(4) {
        position: sticky;
        left: 600px;
        z-index: 20;
        background-color: white;
        border-right: 2px solid #4CAF50;
        min-width: 200px;
        max-width: 200px;
        width: 200px;
      }

      th:nth-child(5), td:nth-child(5) {
        position: sticky;
        left: 800px;
        z-index: 20;
        background-color: white;
        border-right: 2px solid #4CAF50;
        min-width: 200px;
        max-width: 200px;
        width: 200px;
      }

      /* Header styling for fixed columns */
      th:nth-child(1), th:nth-child(2),
      th:nth-child(3), th:nth-child(4), th:nth-child(5) {
        background-color: #4CAF50;
        color: white;
        z-index: 30;
      }

      /* Apply hover effect to the entire row */
      tr:hover td {
        background-color: #e8f5e8;
      }

      /* Apply even-row striping to the entire row */
      tr:nth-child(even) td {
        background-color: #f9f9f9;
      }

      /* Ensure hover overrides even-row striping */
      tr:nth-child(even):hover td {
        background-color: #e8f5e8;
      }

      /* General header styling */
      th {
        background-color: #4CAF50;
        color: white;
        padding: 15px 12px;
        text-align: left;
        position: sticky;
        top: 0;
        z-index: 10;
        border-right: 1px solid #45a049;
        font-weight: bold;
        min-width: 180px;
        width: 240px;
        font-size: 14px;
        box-sizing: border-box;
      }

      td {
        padding: 12px;
        border-bottom: 1px solid #ddd;
        border-right: 1px solid #ddd;
        min-width: 180px;
        width: 200px;
        height: 170px;
        vertical-align: top;
        position: relative;
        background-color: white;
        box-sizing: border-box;
      }

      .cell-content {
        max-height: 150px;
        overflow-y: auto;
        word-wrap: break-word;
        word-break: break-word;
        line-height: 1.4;
        padding-right: 6px;
        font-size: 13px;
      }

      .cell-content::-webkit-scrollbar {
        width: 8px;
      }

      .cell-content::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }

      .cell-content::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 3px;
      }

      .cell-content::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      tr:nth-child(even) {
        background-color: #f9f9f9;
      }

      tr:hover {
        background-color: #e8f5e8;
      }

      .json-content {
        font-family: 'Courier New', monospace;
        font-size: 12px;
        background-color: #f8f8f8;
        border: 1px solid #e0e0e0;
        border-radius: 2px;
        white-space: pre-line;
      }
    `;
  }

  /**
   * Returns the JavaScript functionality for the diagnostic viewer
   * Based on diagnostic-viewer.js
   */
  private static getScript(): string {
    return `
      (function() {
        const ITEMS_PER_PAGE = 10;
        let allData = [];
        let filteredData = [];
        let currentPage = 1;
        let totalPages = 1;
        let columnKeys = [];

        function initializeDiagnosticViewer(data, keys) {
          allData = data || [];
          columnKeys = keys || [];
          
          allData = allData.map(item => {
            if (item.Date) {
              item.Date = formatDate(item.Date);
            }
            return item;
          });

          filteredData = [...allData];
          totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
          
          generateHeaders();
          renderTable();
          setupEventListeners();
        }

        function generateHeaders() {
          const tableHeader = document.getElementById('tableHeader');
          if (tableHeader) {
            tableHeader.innerHTML = columnKeys.map(key => \`
              <th>
                <div class="header-content">
                  <span>\${key}</span>
                  <button class="filter-toggle-btn" data-key="\${key}" title="Filter this column">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                    </svg>
                  </button>
                </div>
                <div class="filter-input-container" id="filter-container-\${key}" style="display: none;">
                  <input type="text" class="column-filter-input" data-key="\${key}" placeholder="Search \${key}...">
                </div>
              </th>
            \`).join('');
          }
        }

        function applyFilters() {
          const searchInput = document.getElementById('searchInput');
          const globalSearchTerm = (searchInput && 'value' in searchInput) ? String(searchInput.value).toLowerCase() : '';

          const columnInputs = document.querySelectorAll('.column-filter-input');
          const columnFilters = {};
          columnInputs.forEach(input => {
            const val = String(input.value).toLowerCase().trim();
            if (val) {
              columnFilters[input.getAttribute('data-key')] = val;
            }
          });

          filteredData = allData.filter(item => {
            let matchesColumnFilters = true;
            for (const key in columnFilters) {
              const value = item[key];
              if (value === null || value === undefined) {
                matchesColumnFilters = false;
                break;
              }
              const stringVal = typeof value === 'object' ? JSON.stringify(value).toLowerCase() : String(value).toLowerCase();
              if (!stringVal.includes(columnFilters[key])) {
                matchesColumnFilters = false;
                break;
              }
            }

            if (!matchesColumnFilters) return false;
            if (globalSearchTerm === '') return true;

            return columnKeys.some(key => {
              const value = item[key];
              if (value === null || value === undefined) return false;
              const stringVal = typeof value === 'object' ? JSON.stringify(value).toLowerCase() : String(value).toLowerCase();
              return stringVal.includes(globalSearchTerm);
            });
          });

          totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE) || 1;
          currentPage = 1;
          renderTable();
        }

        function formatDate(dateStr) {
          try {
            let date;
            if (typeof dateStr === 'string') {
              if (dateStr.includes('T')) {
                const cleanDateStr = dateStr.split('.')[0];
                date = new Date(cleanDateStr + 'Z');
              } else {
                date = new Date(dateStr);
              }
            } else {
              date = new Date(dateStr);
            }

            if (!isNaN(date.getTime())) {
              return date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              });
            } else {
              return dateStr;
            }
          } catch (e) {
            return dateStr;
          }
        }

        function renderTable() {
          const tableBody = document.getElementById('tableBody');
          const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
          const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredData.length);

          if (tableBody) {
            tableBody.innerHTML = '';

            for (let i = startIndex; i < endIndex; i++) {
              const item = filteredData[i];
              const row = document.createElement('tr');
              row.id = 'row-' + i;

              columnKeys.forEach((key) => {
                const cell = document.createElement('td');
                const cellContent = document.createElement('div');
                cellContent.className = 'cell-content';

                let value = (item[key] ?? 'N/A');

                const isJsonContent = typeof value === 'string' &&
                  (value.trim().startsWith('{') || value.trim().startsWith('['));

                if (isJsonContent) {
                  try {
                    const parsed = JSON.parse(value);
                    const formatted = JSON.stringify(parsed, null, 2);
                    const jsonDiv = document.createElement('div');
                    jsonDiv.className = 'json-content';
                    jsonDiv.textContent = formatted;
                    cellContent.appendChild(jsonDiv);
                  } catch (e) {
                    cellContent.textContent = value;
                  }
                } else if (typeof value === 'object' && value !== null) {
                  const formatted = JSON.stringify(value, null, 2);
                  const jsonDiv = document.createElement('div');
                  jsonDiv.className = 'json-content';
                  jsonDiv.textContent = formatted;
                  cellContent.appendChild(jsonDiv);
                } else {
                  cellContent.textContent = value;
                }

                cell.appendChild(cellContent);
                row.appendChild(cell);
              });

              tableBody.appendChild(row);
            }
          }

          updatePaginationControls();
        }

        function updatePaginationControls() {
          const paginationInfo = document.getElementById('paginationInfo');
          const firstBtn = document.getElementById('firstBtn');
          const prevBtn = document.getElementById('prevBtn');
          const nextBtn = document.getElementById('nextBtn');
          const lastBtn = document.getElementById('lastBtn');
          const pageInput = document.getElementById('pageInput');
          const totalPagesLabel = document.getElementById('totalPagesLabel');

          const startIndex = (currentPage - 1) * ITEMS_PER_PAGE + 1;
          const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length);

          if (paginationInfo) {
            paginationInfo.textContent = \`Showing \${startIndex}-\${endIndex} of \${filteredData.length} entries\`;
          }

          if (totalPagesLabel) {
            totalPagesLabel.textContent = \`of \${totalPages}\`;
          }

          if (pageInput) {
            pageInput.value = String(currentPage);
            pageInput.setAttribute('max', String(totalPages));
          }

          if (firstBtn) firstBtn.disabled = currentPage === 1;
          if (prevBtn) prevBtn.disabled = currentPage === 1;
          if (nextBtn) nextBtn.disabled = currentPage === totalPages;
          if (lastBtn) lastBtn.disabled = currentPage === totalPages;
        }

        function goToPage(page) {
          const clamped = Math.max(1, Math.min(page, totalPages));
          if (clamped !== currentPage) {
            currentPage = clamped;
            renderTable();
          } else {
            updatePaginationControls();
          }
        }

        function setupEventListeners() {
          const firstBtn = document.getElementById('firstBtn');
          const prevBtn = document.getElementById('prevBtn');
          const nextBtn = document.getElementById('nextBtn');
          const lastBtn = document.getElementById('lastBtn');
          const pageInput = document.getElementById('pageInput');

          if (firstBtn) firstBtn.addEventListener('click', () => goToPage(1));
          if (prevBtn) prevBtn.addEventListener('click', () => goToPage(currentPage - 1));
          if (nextBtn) nextBtn.addEventListener('click', () => goToPage(currentPage + 1));
          if (lastBtn) lastBtn.addEventListener('click', () => goToPage(totalPages));

          if (pageInput) {
            pageInput.addEventListener('change', function(e) {
              const value = (e.target && 'value' in e.target) ? Number(e.target.value) : NaN;
              if (!isNaN(value) && value >= 1 && value <= totalPages) {
                goToPage(value);
              } else {
                e.target.value = String(currentPage);
              }
            });
          }

          const searchInput = document.getElementById('searchInput');
          if (searchInput) {
            searchInput.addEventListener('keyup', applyFilters);
          }

          const toggleBtns = document.querySelectorAll('.filter-toggle-btn');
          toggleBtns.forEach(btn => {
            btn.addEventListener('click', function(e) {
              const key = this.getAttribute('data-key');
              const container = document.getElementById('filter-container-' + key);
              if (container) {
                if (container.style.display === 'none') {
                  container.style.display = 'block';
                  this.classList.add('active');
                  const input = container.querySelector('input');
                  if (input) input.focus();
                } else {
                  container.style.display = 'none';
                  this.classList.remove('active');
                  const input = container.querySelector('input');
                  if (input && input.value !== '') {
                    input.value = '';
                    applyFilters();
                  }
                }
              }
            });
          });

          const columnInputs = document.querySelectorAll('.column-filter-input');
          columnInputs.forEach(input => {
            input.addEventListener('keyup', applyFilters);
          });

          const downloadBtn = document.getElementById('downloadExcelBtn');
          if (downloadBtn) {
            downloadBtn.addEventListener('click', downloadExcel);
          }
        }

        function downloadExcel() {
          if (!filteredData || filteredData.length === 0) {
            alert('No data to export');
            return;
          }

          const headers = columnKeys.map(k => \`"\${k}"\`).join(',');
          const rows = filteredData.map(item => {
            return columnKeys.map(key => {
              let cellData = item[key] ?? '';
              if (typeof cellData === 'object') {
                cellData = JSON.stringify(cellData);
              }
              cellData = String(cellData).replace(/"/g, '""');
              return \`"\${cellData}"\`;
            }).join(',');
          });
          
          const csvContent = headers + '\\n' + rows.join('\\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          const dateStr = new Date().toISOString().slice(0,10).replace(/-/g,'');
          link.setAttribute('download', \`diagnostic_data_\${dateStr}.csv\`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        window.initializeDiagnosticViewer = initializeDiagnosticViewer;
      })();
    `;
  }
}
